import React, { useState, useEffect } from "react";
import {
    DatatableWrapper,
    Filter,
    Pagination,
    PaginationOptions,
    TableBody,
    TableHeader
} from 'react-bs-datatable';
import * as dayjs from "dayjs";
import { Col, Badge, Row, Table, Dropdown } from "react-bootstrap";
import { LinkContainer } from "react-router-bootstrap";

import { ASSESSMENT_PERIOD } from "../../fastConstants";

import './Dashboard.css';
import { Link } from "react-router-dom";

const FAST_URL = process.env.REACT_APP_DS_API_URL

const TABLE_HEADERS = [
    { title: 'School', prop: 'school', isSortable: true },
    { title: 'Department', prop: 'department', isSortable: true, isFilterable: true },
    { title: 'Program', prop: 'program', isSortable: true, isFilterable: true },
    { 
        title: 'Status', 
        prop: 'status',
        cell: (row) => (
            <Badge bg="success">Completed</Badge>
        )
    },
    { 
        prop: 'href',
        cell: (row) => (
            <LinkContainer 
                to={{pathname:row.href, search:'?d=' + row.departmentId}}
                >
                <a href={row.href}>View</a>
            </LinkContainer>
        )
    }
  ];
  const BRIGHTSPACE_AUTOMATION_TABLE_HEADERS = [
    { title: 'School', prop: 'school', isSortable: true },
    { title: 'Department', prop: 'department', isSortable: true, isFilterable: true },
    { 
        title: 'Program', 
        prop: 'program', 
        isSortable: true, 
        isFilterable: true
    }
  ]
  const IN_PROGRESS_TABLE_HEADERS = [
    { title: 'School', prop: 'school', isSortable: true },
    { title: 'Department', prop: 'department', isSortable: true, isFilterable: true },
    { 
        title: 'Program', 
        prop: 'program', 
        isSortable: true, 
        isFilterable: true
    },
    { 
        title: 'Date Modified', 
        prop: 'dateModified',
        cell: (row) => {
            if (row.dateModified) {
                return <span style={{"fontSize":"0.8em","fontStyle":"italic","color":"#666"}}>
                    {row.dateModified}
                </span>
            } else {
                return <></>
            }
        }
    },
    { 
        title: 'Last Step Completed', 
        prop: 'lastStepCompleted', 
        isSortable: true, 
        thProps: { className: "header-lb--auto"},
        cell: (row) => {
            return <div style={{textAlign: "center"}}>{row.lastStepCompleted}</div>
        }
    },
    {
        prop: 'status',
        cell: (row) => {
            if (row.status === 'In Progress') {
                return <Badge bg="primary">In Progress</Badge>
            } else if (row.status === 'Ready') {
                return <Badge bg="secondary">Ready</Badge>
            } else {
                return <>{row.status}</>
            }
        }
    }
  ];
const MISSING_TABLE_HEADERS = [
    { title: 'School', prop: 'school', isSortable: true },
    { title: 'Department', prop: 'department', isSortable: true, isFilterable: true },
    { title: 'Program', prop: 'program', isSortable: true, isFilterable: true },
  ];

export default function Dashboard(props) {
    const [completedAssessments, setCompletedAssessments] = useState([])
    const [brightspaceAutomationAssessments, setBrightspaceAutomationAssessments] = useState([])
    const [inProgressAssessments, setInProgressAssessments] = useState([])
    const [missingAssessments, setMissingAssessments] = useState([])

    useEffect(() => {
        let url = FAST_URL + "/assessments?reviewYear=" + ASSESSMENT_PERIOD
        fetch(url)
        .then(response => {
            return response.json()
        })
        .then(data => {
            setCompletedAssessments(filterByCompleted(data))
            setBrightspaceAutomationAssessments(filterByBrightspaceAutomation(data))
            setInProgressAssessments(filterByInProgress(data))
            setMissingAssessments(filterByMissing(data))
        })
        .catch(err => {
            console.log(err)
        })
    }, [])

    const filterByCompleted = (data) => {
        let programAssessmentsComplete = []
        data.forEach((department) => {
            if (department.submittedPrograms) {
                department.submittedPrograms.forEach((program) => {
                    if (program.status === "completed") {
                        programAssessmentsComplete.push({
                            department: department.name,
                            school: department.school,
                            program: program.name,
                            status: "Completed",
                            href: '/assessment/view',
                            departmentId: department.departmentAssessmentId,
                        })
                    }
                })
            }
        })
        return programAssessmentsComplete
    }

    const filterByBrightspaceAutomation = (data) => {
        let programAssessmentsWithBrightspaceAutomation = []
        data.forEach((department) => {
            if (department.submittedPrograms) {
                department.submittedPrograms.forEach((program) => {
                    if (program.brightspaceAutomation) {
                        programAssessmentsWithBrightspaceAutomation.push({
                            department: department.name,
                            school: department.school,
                            program: program.name
                        })
                    }
                })
            }
        })
        return programAssessmentsWithBrightspaceAutomation
    }

    const filterByInProgress = (data) => {
        let programAssessmentsInProgress = []
        data.forEach((department) => {
            if (department.submittedPrograms) {
                department.submittedPrograms.forEach((program) => {
                    if (program.status === "in-progress") {
                        programAssessmentsInProgress.push({
                            department: department.name,
                            school: department.school,
                            program: program.name,
                            dateModified: program.dateModified ? dayjs(program.dateModified).fromNow() : '',
                            lastStepCompleted: program.lastStepCompleted > 0 ? program.lastStepCompleted : '',
                            status: 'In Progress',
                            brightspaceAutomation: program.brightspaceAutomation
                        })
                    } else if (program.status === "ready") {
                        programAssessmentsInProgress.push({
                            department: department.name,
                            school: department.school,
                            program: program.name,
                            lastStepCompleted: '',
                            status: 'Ready'
                        })
                    }
                })
            }
        })
        return programAssessmentsInProgress
    }

    const filterByMissing = (data) => {
        let programAssessmentsMissing = []
        data.forEach((department) => {
            if (department.unsubmittedPrograms) {
                department.unsubmittedPrograms.forEach((program) => {
                    programAssessmentsMissing.push({
                        department: department.name,
                        school: department.school,
                        program: program.name
                    })
                })
            }
        })
        return programAssessmentsMissing
    }

    return (
        <main>
            <div className="text-center fs-2 p-3">
                Assessment Team Dashboard
            </div>
            <div className="text-center">
                <Dropdown size="sm">
                    <Dropdown.Toggle variant="primary" id="dashboard-jump">
                        Jump To
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                        <Dropdown.Item href="#completed-assessments">
                            Completed Assessments
                        </Dropdown.Item>
                        <Dropdown.Item href="#in-progress-assessments">
                            In Progress Assessments
                        </Dropdown.Item>
                        <Dropdown.Item href="#brightspace-automation">
                            Brightspace Automation Data Collection
                        </Dropdown.Item>
                        <Dropdown.Item href="#unmapped-assessments">
                            Unmapped Assessments
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>
            <div id="completed-assessments">
                <div className="fs-3">
                    Completed Assessments
                </div>
                <DatatableWrapper
                    body={completedAssessments}
                    headers={TABLE_HEADERS}
                    >
                    <Row className="mb-4">
                        <Col
                            lg={4}>
                        </Col>
                        <Col
                            lg={4}>
                        </Col>
                        <Col
                            lg={4}>
                            { completedAssessments.length > 10 &&
                                <Filter />    
                            }
                        </Col>
                    </Row>

                    <Table>
                        <TableHeader />
                        <TableBody />
                    </Table>

                    <Row className="mb-4">

                    </Row>
                </DatatableWrapper>
            </div>

            <div id="in-progress-assessments">
                <div className="fs-3">
                    In Progress Assessments
                </div>
                <DatatableWrapper
                    body={inProgressAssessments}
                    headers={IN_PROGRESS_TABLE_HEADERS}
                    >
                    <Row className="mb-4">
                        <Col lg={4}></Col>
                        <Col lg={4}></Col>
                        <Col lg={4}>
                        { inProgressAssessments.length > 10 &&
                                <Filter />
                            }
                        </Col>
                    </Row>
                    <Table>
                        <TableHeader />
                        <TableBody />
                    </Table>
                    <Row className="mb-4">

                    </Row>
                </DatatableWrapper>
            </div>

            <div id="brightspace-automation">
                <div className="fs-3">
                    Brightspace Automation Data Collection
                </div>
                <DatatableWrapper
                    body={brightspaceAutomationAssessments}
                    headers={BRIGHTSPACE_AUTOMATION_TABLE_HEADERS}
                    >
                    <Row className="mb-4">
                        <Col
                            lg={4}>
                        </Col>
                        <Col
                            lg={4}>
                        </Col>
                        <Col
                            lg={4}>
                            { completedAssessments.length > 10 &&
                                <Filter />    
                            }
                        </Col>
                    </Row>

                    <Table>
                        <TableHeader />
                        <TableBody />
                    </Table>

                    <Row className="mb-4">

                    </Row>
                </DatatableWrapper>
            </div>

            <div id="unmapped-assessments">
                <div className="fs-3">
                    Unmapped Assessments
                </div>
                <DatatableWrapper
                    body={missingAssessments}
                    headers={MISSING_TABLE_HEADERS}
                    >
                    <Row className="mb-4">
                        <Col
                            lg={4}>
                        </Col>
                        <Col
                            lg={4}>
                        </Col>
                        <Col
                            lg={4}>
                            { missingAssessments.length > 10 &&
                                <Filter />
                            }
                        </Col>
                    </Row>

                    <Table>
                        <TableHeader />
                        <TableBody />
                    </Table>

                    <Row className="mb-4">

                    </Row>
                </DatatableWrapper>
            </div>
        </main>
    )
}